import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";

import { authClient } from "../context/auth";

export const Route = createFileRoute("/login")({
  component: Login,
});

function Login() {
  const r = useRouter();
  const navigate = r.navigate;
  const [isSignup, setIsSignup] = useState(false);

  const m = useMutation({
    async mutationFn(data: { email: string; password: string; signup: boolean }, _ctx) {
      const { signup, ...body } = data;
      const res = signup
        ? await authClient.signUp.email({ ...body, name: "_" })
        : await authClient.signIn.email(body);
      if (res.error) {
        throw res.error;
      }
      await navigate({ to: "/home" });
    },
  });

  if (m.isPending || m.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[--bg]">
        <div className="text-[--ink-muted] text-sm">
          {isSignup ? "Creating account…" : "Signing in…"}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[--bg] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl font-semibold tracking-tight text-[--ink] mb-2">
            Open Money
          </h1>
          <p className="text-sm text-[--ink-muted]">
            {isSignup ? "Create your account" : "Sign in to your account"}
          </p>
        </div>

        <div className="island-shell rounded-md p-6">
          {m.error && (
            <div className="mb-4 p-3 bg-[--bg-soft] border border-[--line] rounded text-sm text-[--green]">
              {JSON.stringify(m.error)}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              m.mutate({
                email: formData.get("username") as string,
                password: formData.get("password") as string,
                signup: isSignup,
              });
            }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="username" className="block mb-2">
                Email
              </label>
              <input
                id="username"
                name="username"
                type="email"
                placeholder="you@example.com"
                required
                autoComplete="email"
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="password" className="block mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                required
                autoComplete={isSignup ? "new-password" : "current-password"}
                className="w-full"
              />
            </div>

            <div className="pt-2">
              <button type="submit" className="primary w-full">
                {isSignup ? "Create Account" : "Sign In"}
              </button>
            </div>
          </form>

          <div className="mt-6 pt-4 border-t border-[--line]">
            <button
              type="button"
              onClick={() => setIsSignup(!isSignup)}
              className="w-full text-sm text-[--ink-soft] hover:text-[--ink] transition-colors"
            >
              {isSignup ? "Already have an account? Sign in" : "Don't have an account? Sign up"}
            </button>
          </div>
        </div>

        <p className="text-center mt-6 text-xs text-[--ink-muted]">
          Secure financial management with data privacy
        </p>
      </div>
    </div>
  );
}
